import React, { FC, useEffect, useState } from 'react';
import { CustomButton, CustomInput } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { InputType } from 'components/custom-input.tsx/CustomInput';
import {
  CleverTapEventsLegalReacceptance,
  WalletBankAccountPayload,
  WalletBankViewSource
} from 'containers/legal-reacceptance/legalReacceptance.model';
import {
  WALLET_BANK_CHECKING_ONLY_LABEL,
  WALLET_BANK_CONFIRM_CTA,
  WALLET_BANK_INSTRUCTION,
  WALLET_BANK_PLACEHOLDER_ACCOUNT,
  WALLET_BANK_PLACEHOLDER_ACCOUNT_HOLDER,
  WALLET_BANK_PLACEHOLDER_ROUTING,
  WALLET_BANK_ROUTING_HELPER,
  WALLET_BANK_SKIP_CTA
} from 'containers/legal-reacceptance/constants';
import { logAnalyticEvent } from 'utils/analytics';

export interface WalletBankAccountScreenProps {
  onSubmit: (payload: WalletBankAccountPayload) => void | Promise<void>;
  /** Omit when skip should not be shown (e.g. payouts reconnect modal). */
  onSkip?: () => void;
  isSubmitting: boolean;
  viewSource?: WalletBankViewSource;
  walletBankScreenSeenCount?: number;
}

const emptyForm: WalletBankAccountPayload = {
  accountHolderName: '',
  routingNumber: '',
  accountNumber: ''
};

const WalletBankAccountScreen: FC<WalletBankAccountScreenProps> = ({
  onSubmit,
  onSkip,
  isSubmitting,
  viewSource,
  walletBankScreenSeenCount
}) => {
  const [form, setForm] = useState<WalletBankAccountPayload>(emptyForm);

  useEffect(() => {
    if (!viewSource) return;
    logAnalyticEvent(CleverTapEventsLegalReacceptance.webWalletBankAccountScreenView, {
      source: viewSource,
      seenCount: walletBankScreenSeenCount
    });
  }, [viewSource, walletBankScreenSeenCount]);

  const handleFieldChange = (field: keyof WalletBankAccountPayload) => (value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const isValid =
    form.accountHolderName.trim().length > 0 &&
    form.routingNumber.trim().length > 0 &&
    form.accountNumber.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid) return;
    if (viewSource) {
      logAnalyticEvent(CleverTapEventsLegalReacceptance.webWalletBankAccountSubmitClick, {
        source: viewSource,
        seenCount: walletBankScreenSeenCount
      });
    }
    await onSubmit({
      accountHolderName: form.accountHolderName.trim(),
      routingNumber: form.routingNumber.trim(),
      accountNumber: form.accountNumber.trim()
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 p-8 pb-10">
      <div className="flex min-h-0 flex-1 flex-col gap-5">
        <p className="text-sbase font-semibold text-primaryText">{WALLET_BANK_INSTRUCTION}</p>
        <div className="inline-flex w-fit items-center gap-2 rounded-full bg-[#e8f0fe] px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-secondary" aria-hidden />
          <span className="text-xs font-semibold text-secondary">{WALLET_BANK_CHECKING_ONLY_LABEL}</span>
        </div>
        <div className="flex flex-col gap-4">
          <CustomInput
            id="wallet-bank-accountHolderName"
            label="Account holder name"
            isMandatory
            placeholder={WALLET_BANK_PLACEHOLDER_ACCOUNT_HOLDER}
            value={form.accountHolderName}
            onChange={handleFieldChange('accountHolderName')}
            autoComplete="name"
          />
          <div>
            <CustomInput
              id="wallet-bank-routingNumber"
              label="Bank routing number"
              isMandatory
              placeholder={WALLET_BANK_PLACEHOLDER_ROUTING}
              value={form.routingNumber}
              onChange={handleFieldChange('routingNumber')}
              type={InputType.TEXT}
              maxLength={9}
              autoComplete="off"
            />
            <p className="mt-1.5 text-xs text-tertiaryText">{WALLET_BANK_ROUTING_HELPER}</p>
          </div>
          <CustomInput
            id="wallet-bank-accountNumber"
            label="Checking account number"
            isMandatory
            placeholder={WALLET_BANK_PLACEHOLDER_ACCOUNT}
            value={form.accountNumber}
            onChange={handleFieldChange('accountNumber')}
            type={InputType.TEXT}
            autoComplete="off"
          />
        </div>
      </div>
      <div className="flex shrink-0 flex-col gap-2">
        <CustomButton
          type={ButtonType.PRIMARY}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
          id="legal-reacceptance-wallet-confirm-btn"
          className="h-12 w-full"
          childrenContainerStyles="text-17px font-semibold"
          isLoading={isSubmitting}>
          {WALLET_BANK_CONFIRM_CTA}
        </CustomButton>
        {onSkip ? (
          <button
            type="button"
            className="w-full py-2 text-center text-sbase font-semibold text-secondary"
            onClick={onSkip}
            id="legal-reacceptance-wallet-skip-btn">
            {WALLET_BANK_SKIP_CTA}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default WalletBankAccountScreen;
